import React, { useEffect, useState, useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import { Card } from '@nearst/ui';
import { searchProductCategories } from '@services/inventory';
import debounce from 'lodash/debounce';

import styles from './ProductCategory.module.scss';

/**
 * CategorySelect component handles selecting and loading product categories
 * with search, and async loading capabilities
 */
const CategorySelect = ({ defaultCategory, defaultId, onCategorySelect, isClearable = false }) => {
	const [defaultValue, setDefaultValue] = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	// Load initial category value based on defaultId or defaultCategory
	useEffect(() => {
		const loadDefaultCategory = async () => {
			if (defaultId) {
				setIsLoading(true);
				const searchParameters = { id: defaultId };
				const category = await searchProductCategories(searchParameters);
				if (category) {
					const value = {
						value: category.id,
						label: category.title,
						data: { title: category.title, full_tree: category.full_tree }
					};
					setDefaultValue(value);
				}
				setIsLoading(false);
				return;
			}

			if (!defaultCategory) {
				setDefaultValue(null);
				return;
			}

			setIsLoading(true);
			const searchParameters = { query: defaultCategory };
			const categories = await searchProductCategories(searchParameters);
			const category = categories.find((c) => c.title === defaultCategory);
			if (category) {
				const value = {
					value: category.id,
					label: category.title,
					data: { title: category.title, full_tree: category.full_tree }
				};
				setDefaultValue(value);
			}
			setIsLoading(false);
		};
		loadDefaultCategory();
	}, [defaultCategory, defaultId]);

	const loadProductCategories = async (query) => {
		setIsLoading(true);
		const searchParameters = {
			query
		};

		const categories = await searchProductCategories(searchParameters);
		setIsLoading(false);
		return categories.map(({ id, title, full_tree }) => ({
			value: id,
			label: title,
			data: { title, full_tree }
		}));
	};

	// Debounce search to prevent excessive API calls
	const debouncedLoadOptions = useCallback(
		debounce((inputValue, callback) => {
			loadProductCategories(inputValue).then(callback);
		}, 500),
		[]
	);

	// Custom option formatter to display category tree in dropdown
	const formatOptions = ({ data }, { context }) => {
		return (
			<div className={styles.categoryOption}>
				{context === 'menu' && <p className={styles.fullTree}>{data.full_tree}</p>}
				<p>{data.title}</p>
			</div>
		);
	};

	const handleCategorySelect = async (selected) => {
		setDefaultValue(selected);
		onCategorySelect(selected);
	};

	return (
		<AsyncSelect
			className={styles.input}
			value={defaultValue}
			loadOptions={(inputValue, callback) => debouncedLoadOptions(inputValue, callback)}
			noOptionsMessage={({ inputValue }) => (inputValue ? 'No matches' : 'Search for a category...')}
			placeholder="Search for a category..."
			formatOptionLabel={formatOptions}
			onChange={handleCategorySelect}
			isOptionSelected={() => false} // override default selected styling
			isClearable={isClearable}
			components={{ IndicatorSeparator: () => null }}
			isLoading={isLoading}
		/>
	);
};

/**
 * ProductCategory wraps CategorySelect in a Card component
 */
const ProductCategory = ({ defaultCategory, defaultId, onCategorySelect, isClearable }) => {
	return (
		<Card title="Product category" data-cy="inventory-item-product-category">
			<CategorySelect
				defaultCategory={defaultCategory}
				defaultId={defaultId}
				onCategorySelect={onCategorySelect}
				isClearable={isClearable}
			/>
		</Card>
	);
};

export { CategorySelect };
export default ProductCategory;
